import PropTypes from "prop-types";
import capitalize from "lodash/capitalize";
import { useRouter } from "next/router";

import { Breadcrumbs } from "@components/breadcrumbs";
import { Hero } from "@components/hero";
import { SectionHeading } from "@components/headings/section";
import { Dropdown } from "@components/dropdown";
import { BasicCard } from "@components/cards/basic";
import { Pagination } from "@components/pagination";
import { Ad } from "@components/ad";
import { InfoFill } from "@icons/info";

import { makeLpTitle } from "@utils/makeLpTitle";
import { tagular } from "@cohesion/tagular";

import { validatePlaceList, placeListAPI } from "@queries/places/list/api";

import {
  placeTypes,
  placeSortOptions,
  placeSortDescriptions,
  pluralizedPlaceTypes,
} from "@constants/places/explorer";
import { LP_URL } from "@constants/lpUrl";
import { NextHead } from "@components/nextHead";

// place constants
const filterTypes = ["All", ...placeTypes];
const placeTypeId = "placeType";
const placeSortId = "placeSort";

export default function PlacesPage({
  placeSlug,
  placeTitle,
  placeType,
  breadcrumb,
  places,
  queryParams,
}) {
  const router = useRouter();
  const { sort, type } = queryParams || {};
  const formSlug = !placeSlug ? "/places" : `/${placeSlug}/places`;
  const metaTitle = placeSlug ? `${placeTitle} Travel Destinations` : "Places";
  const metaDescription =
    "Where to go, best places to stay, travel tips and and best holiday destinations - inspiration from the experts at Lonely Planet.";
  const canonicalUrl = placeSlug
    ? `${LP_URL}/${placeSlug}/places`
    : `${LP_URL}/places`;

  // Filter titles for place Types
  const getOptionTitle = option => {
    switch (option) {
      case "All":
        return "All types";
      case "Continent":
        return "Continents & subcontinents";
      default:
        return capitalize(pluralizedPlaceTypes[option.toLowerCase()]);
    }
  };
  // Disabled types by place type
  const getDisabledType = option => {
    switch (option) {
      // disable continents place pages except the index page
      case "Continent":
        return placeType;
      default:
        return false;
    }
  };

  const handlePaginationParams = () => {
    // Set parameters for Pagination to prevent placeSlug and overlay from being added to URL
    const { ...queryParamsPagination } = queryParams;
    return queryParamsPagination;
  };

  const handleSubmit = e => {
    e.preventDefault();

    // get values from form
    const { form = e.target } = e.target;
    const filterType = new FormData(form).get("type");
    const sortQuery = new FormData(form).get("sort");

    const filterQuery = {
      sort: sortQuery || "",
      type: filterType || "",
    };

    // prevent placeSlug and page from being added to URL
    const params = new URLSearchParams(filterQuery).toString();

    if (e.target.id === "placeSort") {
      tagular("click", {
        actionOutcome: "SORT",
        outboundUrl: "null",
        webElement: {
          location: "SECTION",
          elementType: "DROPDOWN",
          position: "SORT",
          text: sortQuery.toUpperCase(),
        },
      });
    }
    if (e.target.id === "placeType") {
      tagular("click", {
        actionOutcome: "FILTER",
        outboundUrl: "null",
        webElement: {
          location: "SECTION",
          elementType: "DROPDOWN",
          position: "FILTER",
          text: filterType.toUpperCase(),
        },
      });
    }
    router.push(`${formSlug}?${params}`);
  };

  return (
    <div>
      <NextHead>
        <title>{makeLpTitle(metaTitle)}</title>
        <link key="canonical" rel="canonical" href={canonicalUrl} />
        <meta key="title" name="title" content={makeLpTitle(metaTitle)} />
        <meta key="description" name="description" content={metaDescription} />
        <meta
          key="og:title"
          property="og:title"
          content={makeLpTitle(metaTitle)}
        />
        <meta key="og:url" property="og:url" content={canonicalUrl} />
        <meta
          key="og:description"
          property="og:description"
          content={metaDescription}
        />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={makeLpTitle(metaTitle)}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={metaDescription}
        />
      </NextHead>
      {!placeTitle && (
        <Hero
          credit
          height="short"
          className="pb-6 lg:pb-16"
          images={[
            {
              url: "https://lp-cms-production.imgix.net/2022-04/Stocksy_txp25ffb26beKO300_Medium_2508530.jpeg",
              credit: "Eyes On Asia / Stocksy United",
            },
          ]}
        >
          <div className="absolute h-full w-full">
            <div className="container">
              <SectionHeading
                elem="h1"
                className="absolute text-white bottom-12 pr-4 lg:bottom-14"
              >
                Explore places on Lonely Planet
              </SectionHeading>
            </div>
          </div>
        </Hero>
      )}

      <section
        aria-labelledby="explore"
        className="container pb-6 lg:pb-16 lg:flex lg:flex-wrap lg:justify-around mx-auto relative"
      >
        <div className="lg:w-2/3 lg:pr-8 max-w-4xl">
          <h1 className="text-3xl lg:text-6xl text-blue font-semibold contents">
            <span>{placeTitle} </span>
          </h1>
          {breadcrumb && (
            <div className="text-xl inline">
              <Breadcrumbs
                useComma
                inline
                breadcrumbs={breadcrumb.map(item => ({
                  ...item,
                  slug: `/destinations/${item.slug}`,
                }))}
              />
            </div>
          )}

          <form
            className="mb-8"
            name="places-list"
            action={formSlug}
            onSubmit={handleSubmit}
          >
            <div className="my-8 lg:flex">
              <div className="mb-2 lg:w-1/2 lg:pr-8">
                <Dropdown
                  className="mt-1"
                  id={placeTypeId}
                  label="Filter by type of place"
                  defaultValue={type}
                  onChange={handleSubmit}
                  htmlAttributes={{
                    id: placeTypeId,
                    name: "type",
                  }}
                >
                  {filterTypes.map(option => (
                    <option
                      key={`type-${option}`}
                      value={option}
                      disabled={getDisabledType(option)}
                    >
                      {getOptionTitle(option)}
                    </option>
                  ))}
                </Dropdown>
              </div>
              <div className="mb-4 lg:w-1/2 lg:pl-8">
                <Dropdown
                  className="mt-1"
                  id={placeSortId}
                  label="Sort places by"
                  defaultValue={sort}
                  onChange={handleSubmit}
                  htmlAttributes={{
                    id: placeSortId,
                    name: "sort",
                  }}
                >
                  {placeSortOptions.map(option => (
                    <option key={`sort-${option}`} value={option}>
                      {placeSortDescriptions[option]}
                    </option>
                  ))}
                </Dropdown>
              </div>
            </div>
          </form>
          {places?.items.length > 0 ? (
            <ul className="flex flex-row flex-wrap sm:-mx-2">
              {places.items?.map(
                ({ key, eyebrow, title, slug, featuredImage }, i) => {
                  const handleClick = () => {
                    tagular("click", {
                      actionOutcome: "INTERNALLINK",
                      outboundUrl: `/${slug}`,
                      webElement: {
                        location: "SECTION",
                        elementType: "LINK",
                        position: `PLACES-LIST-${i + 1}`,
                        text: title.toUpperCase(),
                      },
                    });
                  };
                  return (
                    <li
                      key={key}
                      className="w-full sm:w-1/2 lg:w-1/3 sm:px-4 mb-8"
                    >
                      <BasicCard
                        title={title}
                        hasBorder
                        eyebrow={eyebrow}
                        description=""
                        href={`/destinations/${slug}`}
                        image={featuredImage || {}}
                        onClick={handleClick}
                        type="destination"
                      />
                    </li>
                  );
                }
              )}
            </ul>
          ) : (
            <div className="bg-blue-100 p-10 rounded md:flex md:align-middle">
              <InfoFill
                className="mt-2 mb-4 m-auto"
                width="32px"
                height="32px"
              />
              <div className="ml-4">
                <p>
                  Based on the filters you selected, there are no results
                  available. Please try selecting a different combination of
                  filters.
                </p>
              </div>
            </div>
          )}
          {places?.pages > 1 && (
            <Pagination
              selectedPage={parseInt(places.page, 10)}
              numPages={places.pages > 100 ? 100 : places.pages}
              className="my-12"
              params={handlePaginationParams()}
              formSlug={formSlug}
            />
          )}
        </div>
        <aside className="mt-10 lg:mt-0 md:flex md:flex-wrap md:justify-center lg:w-1/3 lg:block">
          <div className="w-full sticky top-0">
            <Ad
              placement="sidebar"
              path="Destinations/places/right-rail"
              additionalTargeting={{
                position: "right-rail",
              }}
            />
          </div>
        </aside>
      </section>
      <div className="my-12 md:mb-20 md:mt-0">
        <Ad
          placement="fullWidth"
          path="Destinations/places/superzone"
          additionalTargeting={{
            position: "superzone",
          }}
        />
      </div>
    </div>
  );
}

export async function getServerSideProps({ query, res }) {
  res.setHeader("Cache-Control", "no-cache, max-age=3600, must-revalidate");

  const { type, sort = "DESC", page = 1, slug = null } = query;
  const placeSlug = Array.isArray(slug) ? slug.join("/") : slug;

  // Default type to City if no place slug
  const defaultType = !slug ? "City" : "All";

  const { isValid, redirect } = await validatePlaceList(placeSlug);

  if (!isValid) {
    return {
      notFound: true,
    };
  }
  if (redirect) {
    return {
      redirect: {
        destination: redirect,
        permanent: true,
      },
    };
  }

  const data = await placeListAPI({
    slug: placeSlug,
    page,
    type: type || defaultType,
    sort: placeSortOptions.includes(sort) ? sort : "DESC",
  });

  return {
    props: {
      ...data,
    },
  };
}

PlacesPage.propTypes = {
  placeSlug: PropTypes.string,
  placeTitle: PropTypes.string,
  placeType: PropTypes.string,
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  places: PropTypes.shape({
    pages: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        esid: PropTypes.string,
        name: PropTypes.string,
        slug: PropTypes.string,
        eyebrow: PropTypes.string,
        featuredImage: PropTypes.shape({
          alt: PropTypes.string,
          caption: PropTypes.string,
          credit: PropTypes.string,
          height: PropTypes.number,
          name: PropTypes.string,
          title: PropTypes.string,
          url: PropTypes.string,
          width: PropTypes.number,
        }),
      })
    ),
  }).isRequired,
  queryParams: PropTypes.shape({
    type: PropTypes.string.isRequired,
    sort: PropTypes.string.isRequired,
  }).isRequired,
};

PlacesPage.defaultProps = {
  breadcrumb: [],
  placeSlug: "",
  placeTitle: "",
  placeType: "",
};
